import { useMemo } from 'react';

import { Apollo } from 'src/gql/generated';

type SummaryDocument = Apollo.GetDocumentFileDetailsQuery['document'];

type SummaryUrlRel = NonNullable<NonNullable<SummaryDocument>['urlRelationships']>[number];
type SummaryChildRel = NonNullable<NonNullable<SummaryDocument>['childRelationships']>[number];
type SummaryParentRel = NonNullable<NonNullable<SummaryDocument>['parentRelationships']>[number];

export type PendingSuggestionItem =
  | { kind: 'orphanChild'; relationship: SummaryChildRel }
  | { kind: 'orphanParent'; relationship: SummaryParentRel }
  | { kind: 'pendingChild'; relationship: SummaryChildRel }
  | { kind: 'pendingParent'; relationship: SummaryParentRel }
  | { kind: 'pendingUrl'; relationship: SummaryUrlRel };

export type SummaryRelatedDocumentState = {
  aiPendingItems: PendingSuggestionItem[];
  confirmedChildRelationships: SummaryChildRel[];
  confirmedParentRelationships: SummaryParentRel[];
  confirmedUrlRelationships: SummaryUrlRel[];
  /** Pending/orphan AI suggestions rendered alongside the Child Documents list (url rows count here too). */
  pendingChildCount: number;
  /** Pending/orphan AI suggestions rendered alongside the Parent Documents list. */
  pendingParentCount: number;
  relatedCount: number;
};

type TaggedRel =
  | { rel: SummaryChildRel; scope: 'child' }
  | { rel: SummaryParentRel; scope: 'parent' }
  | { rel: SummaryUrlRel; scope: 'url' };

type SuggestionBuckets = {
  orphanChild: PendingSuggestionItem[];
  orphanParent: PendingSuggestionItem[];
  pendingChild: PendingSuggestionItem[];
  pendingParent: PendingSuggestionItem[];
  pendingUrl: PendingSuggestionItem[];
};

type ReduceAcc = {
  ai: SuggestionBuckets;
  confirmedChildRelationships: SummaryChildRel[];
  confirmedParentRelationships: SummaryParentRel[];
  confirmedUrlRelationships: SummaryUrlRel[];
};

function emptyBuckets(): SuggestionBuckets {
  return {
    orphanChild: [],
    orphanParent: [],
    pendingChild: [],
    pendingParent: [],
    pendingUrl: [],
  };
}

function flattenSuggestionBuckets(b: SuggestionBuckets): PendingSuggestionItem[] {
  return [
    ...b.pendingParent,
    ...b.pendingChild,
    ...b.pendingUrl,
    ...b.orphanParent,
    ...b.orphanChild,
  ];
}

function isAiSource(rel: { source: Apollo.DocumentRelationshipSource }) {
  return rel.source === Apollo.DocumentRelationshipSource.Ai;
}

function hasResolvableParentDoc(rel: SummaryParentRel) {
  return Boolean(rel.parentDocument ?? rel.parentDocumentId);
}

function hasResolvableChildDoc(rel: SummaryChildRel) {
  return Boolean(rel.childDocument ?? rel.childDocumentId);
}

/** Human-created edges count as confirmed unless explicitly rejected; AI needs a confirm review. */
function isEffectivelyConfirmedRelationship(rel: {
  source: Apollo.DocumentRelationshipSource;
  review?: { outcome: Apollo.ReviewOutcome } | null;
}) {
  if (rel.review?.outcome === Apollo.ReviewOutcome.Rejected) return false;
  return (
    rel.source === Apollo.DocumentRelationshipSource.Manual ||
    rel.review?.outcome === Apollo.ReviewOutcome.Confirmed
  );
}

function isNotOrphan(
  item: PendingSuggestionItem
): item is Exclude<PendingSuggestionItem, { kind: 'orphanParent' | 'orphanChild' }> {
  return item.kind !== 'orphanParent' && item.kind !== 'orphanChild';
}

/** Doc/URL targets for bulk confirm/reject: AI-sourced pending edges only (not orphan rows). */
export function getAiBulkReviewPayload(aiPendingItems: PendingSuggestionItem[]): {
  docRels: Array<SummaryParentRel | SummaryChildRel>;
  targets: Array<{ id: string; type: Apollo.BulkRelationshipTargetType.Relationship }>;
  urlRels: SummaryUrlRel[];
} {
  const urlRels: SummaryUrlRel[] = [];
  const docRels: Array<SummaryParentRel | SummaryChildRel> = [];
  const aiPendingItemsNotOrphans = aiPendingItems.filter(isNotOrphan);
  for (const item of aiPendingItemsNotOrphans) {
    switch (item.kind) {
      case 'pendingUrl':
        urlRels.push(item.relationship);
        break;
      default:
        docRels.push(item.relationship);
        break;
    }
  }
  return {
    docRels,
    targets: aiPendingItemsNotOrphans.map(item => ({
      id: item.relationship.id,
      type: Apollo.BulkRelationshipTargetType.Relationship,
    })),
    urlRels,
  };
}

/**
 * Derives lists the summary Related Documents section needs from file-details relationship
 * arrays. Pending suggestion rows are AI-sourced only; manual edges are treated as confirmed
 * when the related document (or URL row) resolves, even without a review record.
 */
export function useSummaryRelatedDocumentState(
  document: SummaryDocument | null | undefined
): SummaryRelatedDocumentState {
  return useMemo(() => {
    const urlRels = document?.urlRelationships ?? [];
    const childRels = document?.childRelationships ?? [];
    const parentRels = document?.parentRelationships ?? [];

    const stream: TaggedRel[] = [
      ...parentRels.map((rel): TaggedRel => ({ rel, scope: 'parent' })),
      ...childRels.map((rel): TaggedRel => ({ rel, scope: 'child' })),
      ...urlRels.map((rel): TaggedRel => ({ rel, scope: 'url' })),
    ];

    const acc = stream.reduce<ReduceAcc>(
      (a, row) => {
        switch (row.scope) {
          case 'parent': {
            const rel = row.rel;
            if (rel.parentDocument && isEffectivelyConfirmedRelationship(rel)) {
              a.confirmedParentRelationships.push(rel);
              return a;
            }

            // Pending AI needs a parent target: loaded document or at least an id (see summary UI fallback row).
            if (!rel.review && hasResolvableParentDoc(rel)) {
              if (isAiSource(rel))
                a.ai.pendingParent.push({ kind: 'pendingParent', relationship: rel });
              return a;
            }

            if (
              isAiSource(rel) &&
              !rel.review &&
              typeof rel.childDocumentId === 'string' &&
              !rel.parentDocumentId
            )
              a.ai.orphanParent.push({ kind: 'orphanParent', relationship: rel });

            return a;
          }
          case 'child': {
            const rel = row.rel;
            if (rel.childDocument && isEffectivelyConfirmedRelationship(rel)) {
              a.confirmedChildRelationships.push(rel);
              return a;
            }

            if (!rel.review && hasResolvableChildDoc(rel)) {
              if (isAiSource(rel))
                a.ai.pendingChild.push({ kind: 'pendingChild', relationship: rel });

              return a;
            }

            if (
              isAiSource(rel) &&
              !rel.review &&
              typeof rel.parentDocumentId === 'string' &&
              !rel.childDocumentId
            )
              a.ai.orphanChild.push({ kind: 'orphanChild', relationship: rel });

            return a;
          }
          case 'url': {
            const rel = row.rel;
            if (isEffectivelyConfirmedRelationship(rel)) {
              a.confirmedUrlRelationships.push(rel);
              return a;
            }

            if (isAiSource(rel) && !rel.review)
              a.ai.pendingUrl.push({ kind: 'pendingUrl', relationship: rel });

            return a;
          }
          default:
            return a;
        }
      },
      {
        ai: emptyBuckets(),
        confirmedChildRelationships: [],
        confirmedParentRelationships: [],
        confirmedUrlRelationships: [],
      }
    );

    return {
      aiPendingItems: flattenSuggestionBuckets(acc.ai),
      confirmedChildRelationships: acc.confirmedChildRelationships,
      confirmedParentRelationships: acc.confirmedParentRelationships,
      confirmedUrlRelationships: acc.confirmedUrlRelationships,
      pendingChildCount:
        acc.ai.pendingChild.length + acc.ai.orphanChild.length + acc.ai.pendingUrl.length,
      pendingParentCount: acc.ai.pendingParent.length + acc.ai.orphanParent.length,
      relatedCount:
        acc.confirmedParentRelationships.length +
        acc.confirmedChildRelationships.length +
        acc.confirmedUrlRelationships.length,
    };
  }, [document?.urlRelationships, document?.childRelationships, document?.parentRelationships]);
}
